import React, { useState, useRef, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { urls } from './config.dev';
import EmployeeOfTheMonth from './EmployeeOfTheMonth';
import { useNavigate } from 'react-router-dom';
import JSEncrypt from 'jsencrypt';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign, faCommentDots, faClipboardList, faNotesMedical,
  faUserNurse, faTimes, faUserMd, faRadiation,
  faPills, faUser, faFlask, faShoppingCart,
  faAddressBook, faCalendar, faTachometerAlt, faChartBar,
  faCogs, faSpinner, faCreditCard, faBoxes,
  faReceipt, faLock, faShieldAlt, faPhone,
  faCalendarCheck, faWallet, faCircle, faBars
} from '@fortawesome/free-solid-svg-icons';

// ─── Keyframe injection ───────────────────────────────────────
const STYLE_ID = 'dashboard-global-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes fadeIn        { from{opacity:0} to{opacity:1} }
    @keyframes slideIn       { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }
    @keyframes modalSlideIn  { from{transform:translateY(-30px) scale(0.95);opacity:0} to{transform:translateY(0) scale(1);opacity:1} }
    @keyframes sp-shimmer    { 0%{background-position:200% center} 100%{background-position:-200% center} }
    @keyframes sp-fadein     { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
    @keyframes sp-dot-blink  { 0%,100%{opacity:1} 50%{opacity:0.25} }
    @keyframes sp-pulse-red  { 0%,100%{box-shadow:0 0 0 0 rgba(255,77,79,0.55)} 60%{box-shadow:0 0 0 7px rgba(255,77,79,0)} }
    @keyframes sp-pulse-orange{ 0%,100%{box-shadow:0 0 0 0 rgba(250,140,22,0.5)} 60%{box-shadow:0 0 0 7px rgba(250,140,22,0)} }
    .radiating-icon { color: #ffffff; }
    .sb-btn:hover { background-color: rgba(255,255,255,0.10) !important; transform: translateX(2px); }
    
    /* Mobile menu overlay */
    .mobile-sidebar-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0,0,0,0.5);
      z-index: 1000;
      transition: opacity 0.3s ease;
    }
    .mobile-sidebar {
      position: fixed;
      top: 0;
      left: 0;
      bottom: 0;
      width: 280px;
      background-color: #ffffff;
      z-index: 1001;
      overflow-y: auto;
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }
    .mobile-sidebar.open {
      transform: translateX(0);
    }
    .menu-toggle-btn {
      position: fixed;
      top: 15px;
      left: 15px;
      z-index: 99;
      background-color: transparent;
      border: none;
      border-radius: 8px;
      padding: 10px 12px;
      color: #001969;
      cursor: pointer;
      box-shadow: none;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .menu-toggle-btn span {
      font-size: 18px;
      font-weight: bold;
      color: #001969;
    }
    @media (max-width: 768px) {
      .main-content-shifted {
        margin-left: 0 !important;
        width: 100% !important;
      }
      /* Force white background on mobile */
      body, #root, .dashboard-mobile-container {
        background-color: #ffffff !important;
      }
    }
  `;
  document.head.appendChild(s);
}

// ─── Helpers ──────────────────────────────────────────────────
const isSmsMode = (data) =>
  data !== null && data !== undefined && data.days_remaining !== undefined && data.days_remaining !== null;

const getDaysColor = (days) => {
  if (days < 10)  return { primary: '#ff4d4f', glow: 'rgba(255,77,79,0.45)',  label: 'Critical', pulse: 'sp-pulse-red 1.4s infinite' };
  if (days <= 30) return { primary: '#fa8c16', glow: 'rgba(250,140,22,0.35)', label: 'Low',      pulse: 'sp-pulse-orange 1.6s infinite' };
  return           { primary: '#52c41a', glow: 'rgba(82,196,26,0.30)',         label: 'Active',   pulse: 'none' };
};

// ─── Sidebar Subscription Panel ───────────────────────────────
const SidebarSubscriptionPanel = ({ subscriptionData, loading, isMobile }) => {
  const smsMode = isSmsMode(subscriptionData);

  const cardStyle = {
    background: isMobile ? 'rgba(0,25,105,0.05)' : 'rgba(255,255,255,0.06)',
    border: `1px solid ${isMobile ? 'rgba(0,25,105,0.15)' : 'rgba(255,255,255,0.10)'}`,
    borderRadius: '10px',
    padding: '10px 12px',
    marginBottom: '8px',
    animation: 'sp-fadein 0.4s ease both',
  };

  const labelStyle = {
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '1.1px',
    textTransform: 'uppercase',
    color: isMobile ? 'rgba(0,25,105,0.65)' : 'rgba(255,255,255,0.45)',
    marginBottom: '5px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  };

  const shimmerBlue = {
    fontSize: '17px',
    fontWeight: '800',
    letterSpacing: '0.4px',
    fontVariantNumeric: 'tabular-nums',
    background: isMobile 
      ? 'linear-gradient(90deg,#001969 0%,#0033cc 40%,#001969 80%)'
      : 'linear-gradient(90deg,#fff 0%,#a8d8ff 40%,#fff 80%)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: 'sp-shimmer 3.5s linear infinite',
  };

  const shimmerGreen = {
    ...shimmerBlue,
    background: isMobile
      ? 'linear-gradient(90deg,#001969 0%,#2e7d32 40%,#001969 80%)'
      : 'linear-gradient(90deg,#fff 0%,#b9f0c8 40%,#fff 80%)',
    backgroundSize: '200% auto',
  };

  if (loading) {
    return (
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '8px', color: isMobile ? '#001969' : 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
        <FontAwesomeIcon icon={faSpinner} spin className="radiating-icon" style={{ color: isMobile ? '#001969' : '#fff' }} />
        <span>Loading...</span>
      </div>
    );
  }

  if (smsMode) {
    const airtime = subscriptionData?.airtime ?? subscriptionData?.current_balance ?? 0;
    const days    = subscriptionData?.days_remaining ?? 0;
    const dc      = getDaysColor(days);

    return (
      <>
        {/* SMS Airtime */}
        <div style={cardStyle}>
          <div style={labelStyle}>
            <FontAwesomeIcon icon={faCommentDots} className="radiating-icon" style={{ color: isMobile ? '#001969' : '#fff' }} />
            SMS Airtime
          </div>
          <div style={shimmerBlue}>
            UGX {Number(airtime).toLocaleString()}
          </div>
        </div>

        {/* Subscription Days - Single figure display, no beeping */}
        <div style={{ ...cardStyle, animationDelay: '0.07s' }}>
          <div style={labelStyle}>
            <FontAwesomeIcon icon={faCalendarCheck} className="radiating-icon" style={{ color: isMobile ? '#001969' : '#fff' }} />
            Subscription Days
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '17px', fontWeight: '800', color: dc.primary, fontVariantNumeric: 'tabular-nums' }}>
              {days}
              <span style={{ fontSize: '11px', fontWeight: '600', color: isMobile ? '#666' : 'rgba(255,255,255,0.45)', marginLeft: '4px' }}>days</span>
            </span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              backgroundColor: `${dc.primary}22`,
              border: `1px solid ${dc.primary}66`,
              borderRadius: '20px', padding: '2px 9px',
              fontSize: '10px', fontWeight: '700', color: dc.primary,
              letterSpacing: '0.6px',
            }}>
              <FontAwesomeIcon icon={faCircle} style={{ fontSize: '6px' }} />
              {dc.label}
            </span>
          </div>
        </div>
      </>
    );
  }

  // Legacy mode
  const balance = subscriptionData?.current_balance ?? 0;
  return (
    <div style={cardStyle}>
      <div style={labelStyle}>
        <FontAwesomeIcon icon={faWallet} className="radiating-icon" style={{ color: isMobile ? '#001969' : '#fff' }} />
        Subscription Balance
      </div>
      <div style={shimmerGreen}>
        UGX {Number(balance).toLocaleString()}
      </div>
    </div>
  );
};

// ─── Welcome Modal ────────────────────────────────────────────
const WelcomeModal = ({ onClose }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)'
  }}>
    <div style={{
      backgroundColor: '#fff', padding: '30px', borderRadius: '10px',
      maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto',
      position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '20px', borderBottom: '2px solid #001969', paddingBottom: '10px'
      }}>
        <h2 style={{ margin: 0, color: '#001969' }}>Welcome to Clinic Pro System</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>
          <FontAwesomeIcon icon={faTimes} className="radiating-icon" style={{ color: '#666' }} />
        </button>
      </div>
      <div>
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ color: '#001969' }}>🎉 Getting Started</h3>
          <p>Welcome to your new Clinic Pro system! We're excited to help you streamline your clinic operations.</p>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ color: '#001969' }}>🚀 Complete Setup Guide</h3>
          <ol style={{ paddingLeft: '20px', lineHeight: '1.6' }}>
            <li><strong>Add Employees:</strong> Click on <strong>Settings (admin only)</strong> to add your employees</li>
            <li><strong>Admin Access:</strong> The admin password is <strong>12345</strong> - change it immediately for security</li>
            <li><strong>Employee Settings:</strong> Go to Employee Settings in the left sidebar</li>
            <li><strong>Add Employee Details:</strong>
              <ul style={{ marginTop: '5px' }}>
                <li>Insert employee name (one name in CAPITAL LETTERS)</li>
                <li>Set appropriate permissions for each employee</li>
                <li>Assign individual passwords (numbers only, no zeros)</li>
              </ul>
            </li>
            <li><strong>Select Employee:</strong> Return to dashboard and select your name before any action</li>
            <li><strong>Configure Facility Settings:</strong> With your name selected, go to:
              <ul style={{ marginTop: '5px' }}>
                <li><strong>Set Your Items</strong> - Add your shop inventory and prices</li>
                
                <li><strong>Set Sales / Expenses Categories</strong> - Organize your sales and expenses</li>
               
              </ul>
            </li>
            <li><strong>Start Managing:</strong> Once setup is complete, select your name and go to any department!</li>
          </ol>
        </div>
      </div>
      <div style={{
        marginBottom: '20px', backgroundColor: '#fff3cd',
        border: '1px solid #ffeeba', borderRadius: '5px', padding: '15px'
      }}>
        <h3 style={{ color: '#856404', margin: '0 0 10px 0' }}>📝 Important Notes</h3>
        <ul style={{ margin: 0, color: '#856404' }}>
          <li>Charges are based on active usage - no usage, no charges</li>
          <li>Multiple users? Each active user costs UGX 500 per 24 hours</li>
          <li>Single user clinics are charged the minimum UGX 700 daily rate</li>
          <li>Keep your account active to avoid automatic deletion after 30 days of inactivity</li>
        </ul>
      </div>
      <div style={{ textAlign: 'center', borderTop: '1px solid #dee2e6', paddingTop: '20px' }}>
        <button onClick={onClose} style={{
          backgroundColor: '#001969', color: 'white', border: 'none',
          padding: '12px 30px', borderRadius: '5px', fontSize: '16px',
          cursor: 'pointer', marginBottom: '10px'
        }}>Get Started</button>
        <p style={{ margin: 0, color: '#6c757d', fontSize: '14px' }}>
          For support contact: +256786747733 | DeepMind E-Systems
        </p>
      </div>
    </div>
  </div>
);

// ─── Security Modal (Phone-optimized, white theme) ─────────────────────────
const SecurityModal = ({ onClose, onConfirm, loading, title, subtitle, showThemeToggle, currentTheme, onThemeChange, employeeName, isMobile }) => {
  const [code, setCode] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  // Mobile-optimized modal style
  const modalStyle = isMobile ? {
    backgroundColor: '#fff',
    borderRadius: '24px',
    width: 'calc(100% - 32px)',
    maxWidth: '400px',
    margin: '0 16px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    animation: 'modalSlideIn 0.3s ease-out'
  } : {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '420px',
    margin: '0 16px',
    boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
    overflow: 'hidden',
    animation: 'modalSlideIn 0.3s ease-out'
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1100, backdropFilter: 'blur(4px)'
    }}>
      <div style={modalStyle}>
        {/* Header - White background for mobile, no colored bar */}
        <div style={{
          padding: isMobile ? '20px 20px 8px 20px' : '24px 24px 20px',
          position: 'relative',
          borderBottom: isMobile ? '1px solid #f0f0f0' : 'none',
          backgroundColor: '#fff'
        }}>
          <button onClick={onClose} style={{
            position: 'absolute', top: isMobile ? '12px' : '14px', right: isMobile ? '12px' : '14px',
            background: isMobile ? '#f5f5f5' : 'rgba(0,0,0,0.05)',
            border: 'none', borderRadius: '50%',
            width: isMobile ? '32px' : '32px',
            height: isMobile ? '32px' : '32px',
            cursor: 'pointer', color: '#666',
            fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s'
          }}>
            <FontAwesomeIcon icon={faTimes} className="radiating-icon" style={{ color: '#666' }} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: isMobile ? '#f0f7ff' : '#f0f7ff',
              borderRadius: '50%',
              width: isMobile ? '44px' : '48px',
              height: isMobile ? '44px' : '48px',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: isMobile ? '20px' : '22px', color: '#001969' }} className="radiating-icon" />
            </div>
            <div>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: isMobile ? '16px' : '18px', fontWeight: '700' }}>
                {title || 'Authentication Required'}
              </h3>
              <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: isMobile ? '12px' : '13px' }}>
                {subtitle || (employeeName ? `Verifying identity for ${employeeName}` : 'Enter your security code to proceed')}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: isMobile ? '16px 20px 24px' : '24px' }}>
          {showThemeToggle && employeeName && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '20px', padding: '10px 14px',
              backgroundColor: '#f8f9fa', borderRadius: '10px', border: '1px solid #e9ecef'
            }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#495057' }}>Interface Theme</span>
              <div style={{ display: 'flex', gap: '6px' }}>
                {['blue', 'white'].map(t => (
                  <button key={t} type="button" onClick={() => onThemeChange(t)} style={{
                    padding: '5px 14px', border: 'none', borderRadius: '6px', cursor: 'pointer',
                    backgroundColor: currentTheme === t ? (t === 'blue' ? '#0056b3' : '#6c757d') : '#e9ecef',
                    color: currentTheme === t ? 'white' : '#495057',
                    fontSize: '12px', fontWeight: '600', transition: 'all 0.2s',
                    boxShadow: currentTheme === t ? `0 2px 6px ${t === 'blue' ? 'rgba(0,86,179,0.4)' : 'rgba(108,117,125,0.4)'}` : 'none'
                  }}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151',
              marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'
            }}>
              <FontAwesomeIcon icon={faLock} style={{ marginRight: '6px', color: '#001969' }} className="radiating-icon" />
              Security Code
            </label>
            <input
              ref={inputRef}
              type="password"
              placeholder="••••••••"
              value={code}
              onChange={e => setCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !loading && onConfirm(code)}
              style={{
                width: '100%', padding: isMobile ? '12px 16px' : '14px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px', fontSize: isMobile ? '16px' : '18px',
                letterSpacing: '6px',
                outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
                boxSizing: 'border-box', backgroundColor: '#fafafa', color: '#1f2937'
              }}
              onFocus={e => { e.target.style.borderColor = '#001969'; e.target.style.boxShadow = '0 0 0 3px rgba(0,25,105,0.12)'; e.target.style.backgroundColor = '#fff'; }}
              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; e.target.style.backgroundColor = '#fafafa'; }}
            />
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#9ca3af' }}>
              Press Enter or click Proceed to authenticate
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{
              flex: '1', padding: isMobile ? '12px' : '13px', backgroundColor: '#f3f4f6', color: '#374151',
              border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              transition: 'background 0.2s'
            }}>
              Cancel
            </button>
            <button onClick={() => onConfirm(code)} disabled={loading} style={{
              flex: '2', padding: isMobile ? '12px' : '13px',
              background: loading ? '#9ca3af' : 'linear-gradient(135deg, #001969 0%, #0033cc 100%)',
              color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px',
              fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s, transform 0.1s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}>
              {loading
                ? <><FontAwesomeIcon icon={faSpinner} spin className="radiating-icon" /> Verifying...</>
                : <><FontAwesomeIcon icon={faShieldAlt} className="radiating-icon" /> Proceed</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Admin Modal (Phone-optimized, white theme) ──────────────────────────
const AdminModal = ({ onClose, onConfirm, loading, isMobile }) => {
  const [pwd, setPwd] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  const modalStyle = isMobile ? {
    backgroundColor: '#fff',
    borderRadius: '24px',
    width: 'calc(100% - 32px)',
    maxWidth: '400px',
    margin: '0 16px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    animation: 'modalSlideIn 0.3s ease-out'
  } : {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '400px',
    margin: '0 16px',
    boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
    overflow: 'hidden',
    animation: 'modalSlideIn 0.3s ease-out'
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1100, backdropFilter: 'blur(4px)'
    }}>
      <div style={modalStyle}>
        <div style={{
          padding: isMobile ? '20px 20px 8px 20px' : '24px',
          position: 'relative',
          borderBottom: isMobile ? '1px solid #f0f0f0' : 'none',
          backgroundColor: '#fff'
        }}>
          <button onClick={onClose} style={{
            position: 'absolute', top: isMobile ? '12px' : '14px', right: isMobile ? '12px' : '14px',
            background: isMobile ? '#f5f5f5' : 'rgba(0,0,0,0.05)',
            border: 'none', borderRadius: '50%',
            width: isMobile ? '32px' : '32px',
            height: isMobile ? '32px' : '32px',
            cursor: 'pointer', color: '#666',
            fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <FontAwesomeIcon icon={faTimes} className="radiating-icon" style={{ color: '#666' }} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: '#f3e8ff',
              borderRadius: '50%',
              width: isMobile ? '44px' : '48px',
              height: isMobile ? '44px' : '48px',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <FontAwesomeIcon icon={faTachometerAlt} style={{ fontSize: isMobile ? '20px' : '22px', color: '#7b1fa2' }} className="radiating-icon" />
            </div>
            <div>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: isMobile ? '16px' : '18px', fontWeight: '700' }}>Admin Access</h3>
              <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: isMobile ? '12px' : '13px' }}>Enter admin password to continue</p>
            </div>
          </div>
        </div>
        <div style={{ padding: isMobile ? '16px 20px 24px' : '24px' }}>
          <label style={{
            display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151',
            marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px'
          }}>
            <FontAwesomeIcon icon={faLock} style={{ marginRight: '6px', color: '#7b1fa2' }} className="radiating-icon" />
            Admin Password
          </label>
          <input
            ref={inputRef}
            type="password"
            placeholder="••••••••"
            value={pwd}
            onChange={e => setPwd(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !loading && onConfirm(pwd)}
            style={{
              width: '100%', padding: isMobile ? '12px 16px' : '14px 16px',
              border: '2px solid #e5e7eb',
              borderRadius: '10px', fontSize: isMobile ? '16px' : '18px',
              letterSpacing: '6px',
              outline: 'none', boxSizing: 'border-box', backgroundColor: '#fafafa', marginBottom: '20px'
            }}
            onFocus={e => { e.target.style.borderColor = '#7b1fa2'; e.target.style.boxShadow = '0 0 0 3px rgba(123,31,162,0.12)'; }}
            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
          />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{
              flex: '1', padding: isMobile ? '12px' : '13px', backgroundColor: '#f3f4f6', color: '#374151',
              border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
            }}>Cancel</button>
            <button onClick={() => onConfirm(pwd)} disabled={loading} style={{
              flex: '2', padding: isMobile ? '12px' : '13px',
              background: loading ? '#9ca3af' : 'linear-gradient(135deg, #7b1fa2 0%, #ab47bc 100%)',
              color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px',
              fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}>
              {loading
                ? <><FontAwesomeIcon icon={faSpinner} spin className="radiating-icon" /> Verifying...</>
                : 'Access Admin'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Dashboard ────────────────────────────────────────────────
function Dashboard() {
  const [selectedEmployee, setSelectedEmployee]         = useState(null);
  const [selectedEmployeeTheme, setSelectedEmployeeTheme] = useState('blue');
  const [action, setAction]                             = useState(null);
  const [currentTime, setCurrentTime]                   = useState(new Date());
  const [employees, setEmployees]                       = useState([]);
  const [showAdminModal, setShowAdminModal]             = useState(false);
  const [appointmentCount, setAppointmentCount]         = useState(0);
  const [birthdayCount, setBirthdayCount]               = useState(0);
  const [showModal, setShowModal]                       = useState(false);
  const [loading, setLoading]                           = useState(false);
  const [clinicName, setClinicName]                     = useState('');
  const [token, setToken]                               = useState('');
  const [currentPage, setCurrentPage]                   = useState(1);
  const [searchTerm, setSearchTerm]                     = useState('');
  const [screenWidth, setScreenWidth]                   = useState(window.innerWidth);
  const [showWelcomeModal, setShowWelcomeModal]         = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen]             = useState(false);

  const [showSidebarModal, setShowSidebarModal]         = useState(false);
  const [sidebarLoading, setSidebarLoading]             = useState(false);
  const [sidebarAction, setSidebarAction]               = useState('');
  const [sidebarEmployeeTheme, setSidebarEmployeeTheme] = useState('blue');

  // ── Single subscription data object ──
  const [subscriptionData, setSubscriptionData]         = useState(null);
  const [subscriptionLoading, setSubscriptionLoading]   = useState(false);

  const searchInputRef      = useRef(null);
  const employeesContainerRef = useRef(null);
  const navigate            = useNavigate();

  const publicKey = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAup3FU135mAvJT6OheYW3
pQyWf6jvS4duUMY4cXrlJXyGqu8HqvTU0ewPy6w2HhCPxWboNclkAkPhOCc4URNT
x1Grg+mCsWmfhVimP2wtfmlBCJ09cyDMYf93iGj8RFf3CshY5yhppT/pX+RgTuXw
ClpOXe24CLG2VF9suNylk+ReAMLyOxaekYofAMBvvrD4+GYPJgvkTMXCXCKp2PnO
8+OjiltNMnoyqPEZoXHTV4EXtTrjYnwzSe0WZSSuzgVMhmtdx+IS4eisSumHV1eI
wBeZwI0bYGxDCedPRassmSFgTFqkkcgIXmEP1n5w/08S/QPr2G+myKTeRqp5RJA5
PQIDAQAB
-----END PUBLIC KEY-----`;

  // ── Responsive layout helpers ──
  const getEmployeesPerPage = () => {
    if (screenWidth >= 1920) return { maxEmployees: 20, perRow: 10 };
    if (screenWidth >= 1600) return { maxEmployees: 16, perRow: 8 };
    if (screenWidth >= 1440) return { maxEmployees: 14, perRow: 7 };
    if (screenWidth >= 1200) return { maxEmployees: 12, perRow: 6 };
    if (screenWidth >= 992)  return { maxEmployees: 8,  perRow: 4 };
    if (screenWidth >= 768)  return { maxEmployees: 6,  perRow: 3 };
    if (screenWidth >= 576)  return { maxEmployees: 4,  perRow: 2 };
    return { maxEmployees: 3, perRow: 1 };
  };
  const { maxEmployees: employeesPerPage, perRow: employeesPerRow } = getEmployeesPerPage();
  const isMobile = screenWidth < 768;
  const sidebarWidth = isMobile ? '0px' : '320px';

  // ── Set theme color for browser on mobile (white status bar) ──
  useEffect(() => {
    if (isMobile) {
      let metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (!metaThemeColor) {
        metaThemeColor = document.createElement('meta');
        metaThemeColor.setAttribute('name', 'theme-color');
        document.head.appendChild(metaThemeColor);
      }
      metaThemeColor.setAttribute('content', '#ffffff');
      
      let metaStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (!metaStatusBar) {
        metaStatusBar = document.createElement('meta');
        metaStatusBar.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
        document.head.appendChild(metaStatusBar);
      }
      metaStatusBar.setAttribute('content', 'black-translucent');
    }
  }, [isMobile]);

  // ── Effects ──────────────────────────────────────────────────

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tk = params.get('token');
    setToken(tk);
    const fetchClinicName = async () => {
      try {
        const response = await fetch(`${urls.fetchclinicname}?token=${tk}`);
        const data = await response.json();
        if (response.ok) setClinicName(data.clinic_name);
        else if (data.error === 'Session expired') navigate('/login');
      } catch (error) { console.error('Error fetching clinic name:', error); }
    };
    if (tk) fetchClinicName();
  }, [navigate]);

  useEffect(() => {
    const fetchBirthdayCount = async () => {
      const params = new URLSearchParams(window.location.search);
      const tk = params.get('token');
      try {
        const response = await fetch(`${urls.birthdaycount}?token=${tk}`);
        if (response.ok) {
          const data = await response.json();
          setBirthdayCount(data.birthday_count);
        }
      } catch (error) { console.error('Error fetching birthday count:', error); }
    };
    if (token) fetchBirthdayCount();
  }, [token]);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchEmployees = async () => {
    const params = new URLSearchParams(window.location.search);
    const tk = params.get('token');
    try {
      const response = await fetch(`${urls.fetchemployees}?token=${tk}`);
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      setEmployees(data);
      if (data.length === 0) setShowWelcomeModal(true);
    } catch (error) {
      console.error('Error fetching employees:', error.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchEmployees(); }, []);

  useEffect(() => {
    const fetchAppointmentCount = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const tk = params.get('token');
        const response = await fetch(`${urls.countappointments}?token=${tk}`);
        if (response.ok) {
          const data = await response.json();
          setAppointmentCount(data.appointment_count);
        }
      } catch (error) { console.error('Error fetching appointment count:', error); }
    };
    if (token) fetchAppointmentCount();
  }, [token]);

  // ── Fetch subscription data (unified) ───────────────────────
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        if (!token) return;
        setSubscriptionLoading(true);
        const response = await fetch(urls.fetchbalance, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        if (response.ok) {
          const data = await response.json();
          setSubscriptionData(data);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
      } finally {
        setSubscriptionLoading(false);
      }
    };
    fetchSubscriptionData();
  }, [token]);

  useEffect(() => {
    const handleGlobalKeydown = (e) => {
      if (!showModal && !showAdminModal && !showSidebarModal &&
        e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' &&
        e.target.tagName !== 'BUTTON' && employees.length > employeesPerPage) {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    };
    document.addEventListener('keydown', handleGlobalKeydown);
    return () => document.removeEventListener('keydown', handleGlobalKeydown);
  }, [showModal, showAdminModal, showSidebarModal, employees.length, employeesPerPage]);

  // ── Guard ────────────────────────────────────────────────────
  const checkEmployeeSelected = () => {
    if (!selectedEmployee) {
      toast.warning('⚠️ Please select your name first before performing any action!', {
        position: 'top-center', autoClose: 3000,
        hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true,
      });
      return false;
    }
    return true;
  };

  // ── Handlers ─────────────────────────────────────────────────
  const handleEmployeeClick = (employeeName, act, employeeTheme = 'blue') => {
    setSelectedEmployee(employeeName);
    setSelectedEmployeeTheme(employeeTheme);
    setAction(act);
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleButtonClick = (act) => {
    if (!checkEmployeeSelected()) return;
    setAction(act);
    setShowModal(true);
  };

  const handleLaboratoryButtonClick    = () => { if (!checkEmployeeSelected()) return; setAction('access-laboratory');   setShowModal(true); };
  const handleContactsButtonClick      = () => handleButtonClick('contacts');
  const handleTriageButtonClick        = () => { if (!checkEmployeeSelected()) return; setAction('triage');              setShowModal(true); };
  const handleStoreButtonClick         = () => handleButtonClick('store');
  const handleNurseButtonClick         = () => { if (!checkEmployeeSelected()) return; setAction('access-nurse');        setShowModal(true); };
  const handlePatientAppointmentsButtonClick = () => handleButtonClick('patient-appointments');
  const handleDoctorsRoomButtonClick   = () => { if (!checkEmployeeSelected()) return; setAction('access-doctors-room'); setShowModal(true); };
  const handleRadiographerButtonClick  = () => { if (!checkEmployeeSelected()) return; setAction('access-radiographer'); setShowModal(true); };

  const handleSidebarButtonClick = async (act = null, navPath = null) => {
    if (!checkEmployeeSelected()) return;
    if (act) {
      setSidebarAction(act);
      setShowSidebarModal(true);
      if (selectedEmployeeTheme) setSidebarEmployeeTheme(selectedEmployeeTheme);
    }
    if (navPath) {
      if (navPath.includes('/generate-drug-order')) {
        const params = new URLSearchParams();
        params.append('token', token);
        navigate(`${navPath}?${params.toString()}`);
      } else {
        navigate(navPath);
      }
    }
    if (isMobile) setMobileMenuOpen(false);
  };

  const confirmAction = async (securityCode) => {
    try {
      setLoading(true);
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(publicKey);
      const encryptedSecurityCode = encrypt.encrypt(securityCode);
      const params = new URLSearchParams(window.location.search);
      const tk = params.get('token');

      const codeResponse = await fetch(urls.code, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee: selectedEmployee, action, securityCode: encryptedSecurityCode, token: tk, theme: selectedEmployeeTheme }),
      });

      if (codeResponse.ok) {
        const codeData = await codeResponse.json();
        if (codeData.result === 'yes') {
          const permitResponse = await fetch(urls.permit, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ employee: selectedEmployee, action, token: tk, theme: selectedEmployeeTheme }),
          });
          if (permitResponse.ok) {
            const permitData = await permitResponse.json();
            if (permitData.result === 'yes') {
              const loginToken = permitData.login_token;
              const routeMap = {
                'access-laboratory':   `/access-laboratory/?token=${loginToken}&theme=${selectedEmployeeTheme}`,
                'access-radiographer': `/access-radiographer/?token=${loginToken}&theme=${selectedEmployeeTheme}`,
                'access-doctors-room': `/access-doctors-room/?token=${loginToken}&theme=${selectedEmployeeTheme}`,
                'access-nurse':        `/access-nurse/?token=${loginToken}&theme=${selectedEmployeeTheme}`,
                'sales':               `/sales/?token=${loginToken}&theme=${selectedEmployeeTheme}`,
                'credits':             `/credits/?token=${loginToken}&theme=${selectedEmployeeTheme}`,
                'selldrugs':           `/selldrugs/?token=${loginToken}&theme=${selectedEmployeeTheme}`,
                'triage':              `/triage/?token=${loginToken}&theme=${selectedEmployeeTheme}`,
                'store':               `/store/?token=${loginToken}&theme=${selectedEmployeeTheme}`,
                'contacts':            `/contacts/?token=${loginToken}&theme=${selectedEmployeeTheme}`,
                'patient-appointments':`/patient-appointments/?token=${loginToken}&theme=${selectedEmployeeTheme}`
              };
              const route = routeMap[action];
              if (route) navigate(route);
            } else if (permitData.redirectUrl) {
              const confirmed = window.confirm('Sorry, your subscription expired. Please press OK to proceed and make a payment.');
              if (confirmed) window.location.href = permitData.redirectUrl;
            } else {
              toast.warning('Permission not granted. Please contact the administrator.');
            }
          }
        } else {
          toast.error('Invalid security code. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const confirmSidebarAction = async (sidebarSecurityCode) => {
    try {
      setSidebarLoading(true);
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(publicKey);
      const encryptedSecurityCode = encrypt.encrypt(sidebarSecurityCode);
      const params = new URLSearchParams(window.location.search);
      const tk = params.get('token');

      const response = await fetch(urls.code, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee: selectedEmployee, action: sidebarAction, securityCode: encryptedSecurityCode, token: tk, theme: sidebarEmployeeTheme }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.result === 'yes') {
          const permitResponse = await fetch(urls.permit, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ employee: selectedEmployee, action: sidebarAction, token: tk, theme: sidebarEmployeeTheme }),
          });
          if (permitResponse.ok) {
            const permitData = await permitResponse.json();
            if (permitData.result === 'yes') {
              const loginToken = permitData.login_token;
              navigate(`/${sidebarAction}/?token=${loginToken}&theme=${sidebarEmployeeTheme}`);
            } else if (permitData.redirectUrl) {
              const confirmed = window.confirm('Sorry, your subscription expired. Please press OK to proceed and make a payment.');
              if (confirmed) window.location.href = permitData.redirectUrl;
            } else {
              toast.warning('Permission not granted. Please contact the administrator.');
            }
          } else {
            throw new Error('Failed to get permit response');
          }
        } else {
          toast.error('Invalid security code. Please try again.');
        }
      } else {
        throw new Error('Failed to check security code');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Please try again later.');
    } finally {
      setSidebarLoading(false);
    }
  };

  const handleAdminConfirm = async (adminPassword) => {
    setLoading(true);
    try {
      if (adminPassword) {
        const encrypt = new JSEncrypt();
        encrypt.setPublicKey(publicKey);
        const encryptedAdminPassword = encrypt.encrypt(adminPassword);
        const params = new URLSearchParams(window.location.search);
        const tk = params.get('token');
        if (!tk) { toast.error('Token not found in URL.'); return; }

        const response = await fetch(urls.permitadmin, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ employee: 'admin', adminPassword: encryptedAdminPassword, token: tk }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.result === 'yes') {
            navigate(`/admin-dashboard?token=${data.login_token}`);
          } else if (data.redirectUrl) {
            const confirmed = window.confirm('Sorry, your subscription expired. Please press OK to proceed and make a payment.');
            if (confirmed) window.location.href = data.redirectUrl;
          } else {
            toast.warning('Permission not granted. Please contact clinic pro customer care for help.');
          }
        } else {
          const errorData = await response.json();
          if (errorData.error === 'Invalid admin password') toast.error('Invalid admin password');
        }
      } else {
        toast.error('Admin password cannot be empty. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An unexpected error occurred. Please try again later.');
    } finally {
      setShowAdminModal(false);
      setLoading(false);
    }
  };

  // ── Derived data ─────────────────────────────────────────────
  const filteredEmployees    = employees.filter(e => e.Name.toLowerCase().includes(searchTerm.toLowerCase()));
  const indexOfLastEmployee  = currentPage * employeesPerPage;
  const indexOfFirstEmployee = indexOfLastEmployee - employeesPerPage;
  const currentEmployees     = filteredEmployees.slice(indexOfFirstEmployee, indexOfLastEmployee);
  const shouldShowSearchAndPagination = employees.length > employeesPerPage;

  // ── Styles ───────────────────────────────────────────────────
  const getEmployeeGridColumns = () => {
    if (screenWidth >= 768) return `repeat(${employeesPerRow}, minmax(0, 1fr))`;
    if (screenWidth >= 480) return 'repeat(2, 1fr)';
    return 'repeat(1, 1fr)';
  };
  
  const employeeButtonStyle = (employeeName) => ({
    backgroundColor: selectedEmployee === employeeName ? '#dc2626' : '#315532',
    color: 'white',
    padding: isMobile ? '12px 8px' : '15px 10px',
    textAlign: 'center',
    fontSize: isMobile ? '13px' : '14px',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    textTransform: 'uppercase',
    borderRadius: '4px',
    boxShadow: selectedEmployee === employeeName ? '0 0 0 3px rgba(220,38,38,0.35)' : '0 2px 5px rgba(0,0,0,0.2)',
    width: '100%',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    border: 'none'
  });

  const sidebarBtnStyle = (isMobileContext) => ({
    backgroundColor: 'transparent',
    color: isMobileContext ? '#001969' : '#ffffff',
    border: 'none',
    padding: '10px',
    marginBottom: '5px',
    cursor: 'pointer',
    fontSize: isMobile ? '0.85em' : '1em',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.3s ease, transform 0.2s ease',
    textAlign: 'left',
    paddingLeft: '10px',
    width: '100%',
    borderRadius: '6px',
  });

  const actionBtnStyle = (act) => ({
    backgroundColor: action === act ? '#f50202' : '#007bff',
    color: '#ecf0f1',
    border: 'none',
    padding: isMobile ? '10px 15px' : '15px 25px',
    margin: isMobile ? '5px' : '10px',
    cursor: !selectedEmployee ? 'not-allowed' : 'pointer',
    borderRadius: '5px',
    fontSize: isMobile ? '0.8em' : '1em',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.3s ease, transform 0.3s ease',
    opacity: !selectedEmployee ? 0.6 : 1
  });

  const actionBtnSmStyle = (act) => ({
    backgroundColor: action === act ? '#f50202' : '#007bff',
    color: '#ecf0f1',
    border: 'none',
    padding: isMobile ? '10px 15px' : '15px 25px',
    margin: isMobile ? '5px' : '5px',
    cursor: !selectedEmployee ? 'not-allowed' : 'pointer',
    borderRadius: '5px',
    fontSize: isMobile ? '0.8em' : '1em',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.3s ease, transform 0.3s ease',
    opacity: !selectedEmployee ? 0.6 : 1
  });

  // Sidebar content component (reused for desktop and mobile)
  const SidebarContent = ({ isMobileContext }) => (
    <>
      <h2 style={{
        fontSize: isMobile ? '1.2em' : '1.5em',
        marginBottom: '16px',
        textAlign: 'left',
        paddingLeft: '10px',
        color: isMobileContext ? '#001969' : '#ffffff'
      }}>
        SMART SHOP UG
      </h2>

      <SidebarSubscriptionPanel
        subscriptionData={subscriptionData}
        loading={subscriptionLoading}
        isMobile={isMobileContext}
      />

      <hr style={{ borderTop: `0.5px solid ${isMobileContext ? 'rgba(0,25,105,0.2)' : 'rgba(255,255,255,0.2)'}`, margin: '16px 0' }} />

      <button
        className="sb-btn"
        style={sidebarBtnStyle(isMobileContext)}
        onClick={() => handleSidebarButtonClick('makePayment')}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = isMobileContext ? 'rgba(0,25,105,0.05)' : 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}>
        <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '10px', fontSize: '1.2em', flexShrink: 0, color: isMobileContext ? '#001969' : '#fff' }} className="radiating-icon" />
        <span>Make a Payment</span>
      </button>

      <hr style={{ borderTop: `0.5px solid ${isMobileContext ? 'rgba(0,25,105,0.2)' : 'rgba(255,255,255,0.2)'}`, margin: '16px 0' }} />

      <button
        style={sidebarBtnStyle(isMobileContext)}
        onClick={() => setShowAdminModal(true)}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = isMobileContext ? 'rgba(0,25,105,0.05)' : 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}>
        <FontAwesomeIcon icon={faTachometerAlt} style={{ marginRight: '10px', fontSize: '1.2em', flexShrink: 0, color: isMobileContext ? '#001969' : '#fff' }} className="radiating-icon" />
        <span>Settings (admin only)</span>
      </button>

      <hr style={{ borderTop: `0.5px solid ${isMobileContext ? 'rgba(0,25,105,0.2)' : 'rgba(255,255,255,0.2)'}`, margin: '16px 0' }} />

      {[
        { icon: faPills,         label: 'Set Items',                        action: 'manageDrugs' },
        { icon: faReceipt,       label: 'Set Sales / Expenses Categories',  action: 'set-sales-expenses-categories' },
        { icon: faBoxes,         label: 'Stock Tracking',                   action: 'stocktracking' },
      ].map(item => (
        <button key={item.action} style={sidebarBtnStyle(isMobileContext)} onClick={() => handleSidebarButtonClick(item.action)}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = isMobileContext ? 'rgba(0,25,105,0.05)' : 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}>
          <FontAwesomeIcon icon={item.icon} style={{ marginRight: '10px', fontSize: '1.2em', flexShrink: 0, color: isMobileContext ? '#001969' : '#fff' }} className="radiating-icon" />
          <span>{item.label}</span>
        </button>
      ))}

      <hr style={{ borderTop: `0.5px solid ${isMobileContext ? 'rgba(0,25,105,0.2)' : 'rgba(255,255,255,0.2)'}`, margin: '16px 0' }} />

      <hr style={{ borderTop: `0.5px solid ${isMobileContext ? 'rgba(0,25,105,0.2)' : 'rgba(255,255,255,0.2)'}`, margin: '16px 0' }} />

      <h3 style={{ fontSize: isMobile ? '1em' : '1.2em', margin: '0 0 10px', paddingLeft: '10px', color: isMobileContext ? '#001969' : '#ffffff' }}>
        See Employee Participation
      </h3>
      <button style={{ ...sidebarBtnStyle(isMobileContext), fontSize: '1.2em', padding: '15px 10px' }}
        onClick={() => handleSidebarButtonClick('employeePerformance')}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = isMobileContext ? 'rgba(0,25,105,0.05)' : 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}>
        <FontAwesomeIcon icon={faUser} style={{ marginRight: '10px', fontSize: '1.2em', flexShrink: 0, color: isMobileContext ? '#001969' : '#fff' }} className="radiating-icon" />
        <span style={{ fontSize: '0.83em' }}>See Each Employee Performance</span>
      </button>

      <hr style={{ borderTop: `0.5px solid ${isMobileContext ? 'rgba(0,25,105,0.2)' : 'rgba(255,255,255,0.2)'}`, margin: '16px 0' }} />

      <h3 style={{ fontSize: isMobile ? '1em' : '1.2em', margin: '0 0 10px', paddingLeft: '10px', color: isMobileContext ? '#001969' : '#ffffff' }}>
        Sales Statistics and History
      </h3>
      <button style={sidebarBtnStyle(isMobileContext)} onClick={() => handleSidebarButtonClick('clinicStatistics')}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = isMobileContext ? 'rgba(0,25,105,0.05)' : 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}>
        Business Statistics
      </button>
      <button style={sidebarBtnStyle(isMobileContext)} onClick={() => handleSidebarButtonClick('access-sales-details')}
        onMouseEnter={e => { e.currentTarget.style.backgroundColor = isMobileContext ? 'rgba(0,25,105,0.05)' : 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'translateX(2px)'; }}
        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}>
        Sales history in detail
      </button>

      <hr style={{ borderTop: `0.5px solid ${isMobileContext ? 'rgba(0,25,105,0.2)' : 'rgba(255,255,255,0.2)'}`, margin: '16px 0' }} />
    </>
  );

  // ── Render ───────────────────────────────────────────────────
  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh', 
      backgroundColor: isMobile ? '#ffffff' : '#ecf0f1', 
      fontFamily: 'Arial, sans-serif',
      position: 'relative'
    }}>
      <ToastContainer />

      {/* Mobile Menu Toggle Button - White icon with SMART SHOP text */}
      {isMobile && (
        <button 
          className="menu-toggle-btn" 
          onClick={() => setMobileMenuOpen(true)}
          style={{
            backgroundColor: 'transparent',
            boxShadow: 'none'
          }}
        >
          <FontAwesomeIcon icon={faBars} size="lg" style={{ color: '#001969' }} />
          <span>SMART SHOP</span>
        </button>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <div style={{
          width: sidebarWidth,
          minWidth: sidebarWidth,
          backgroundColor: '#001969',
          color: '#ffffff',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: 0,
          bottom: 0,
          overflowY: 'auto',
          fontFamily: 'Roboto, sans-serif',
          zIndex: 100,
          flexShrink: 0
        }}>
          <SidebarContent isMobileContext={false} />
        </div>
      )}

      {/* Mobile Sidebar Overlay - White background sidebar with dark text */}
      {isMobile && mobileMenuOpen && (
        <div className="mobile-sidebar-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className={`mobile-sidebar ${mobileMenuOpen ? 'open' : ''}`} 
               onClick={(e) => e.stopPropagation()}
               style={{
                 backgroundColor: '#ffffff',
                 boxShadow: '2px 0 15px rgba(0,0,0,0.15)'
               }}>
            <div style={{ padding: '20px', position: 'relative' }}>
              <button
                onClick={() => setMobileMenuOpen(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: '#f0f0f0',
                  border: 'none',
                  borderRadius: '4px',
                  width: '28px',
                  height: '28px',
                  color: '#001969',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <SidebarContent isMobileContext={true} />
            </div>
          </div>
        </div>
      )}

      {/* ── Main Content ─────────────────────────────────────── */}
      <div style={{
        flex: 1,
        marginLeft: isMobile ? '0' : sidebarWidth,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isMobile ? '#ffffff' : 'black',
        minHeight: '100vh',
        width: isMobile ? '100%' : `calc(100% - ${sidebarWidth})`,
        paddingTop: isMobile ? '60px' : '0'
      }}>
        {/* Employee of the Month */}
        <div style={{ padding: '20px 20px 0 20px', backgroundColor: isMobile ? '#ffffff' : 'black' }}>
          {token && <EmployeeOfTheMonth token={token} />}
        </div>

        {/* Content area */}
        <div style={{
          flex: 1, padding: isMobile ? '10px' : '20px', display: 'flex', flexDirection: 'column',
          backgroundColor: isMobile ? '#ffffff' : 'black', alignItems: 'center', justifyContent: 'flex-start',
          boxSizing: 'border-box'
        }}>
          <div style={{
            padding: isMobile ? '15px' : '20px', backgroundColor: '#e9e8f0',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            width: '100%', minHeight: '70vh',
            boxShadow: '0 0 10px rgba(0,0,0,0.1)',
            borderTop: '1px solid rgb(9,243,114)',
            borderBottom: '1px solid rgb(9,243,114)',
            borderLeft: '1px solid rgb(9,243,114)',
            borderRight: 'none',
            boxSizing: 'border-box',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'354\' height=\'354\' viewBox=\'0 0 200 200\'%3E%3Cdefs%3E%3ClinearGradient id=\'a\' gradientUnits=\'userSpaceOnUse\' x1=\'100\' y1=\'33\' x2=\'100\' y2=\'-3\'%3E%3Cstop offset=\'0\' stop-color=\'%23000\' stop-opacity=\'0\'/%3E%3Cstop offset=\'1\' stop-color=\'%23000\' stop-opacity=\'1\'/%3E%3C/linearGradient%3E%3ClinearGradient id=\'b\' gradientUnits=\'userSpaceOnUse\' x1=\'100\' y1=\'135\' x2=\'100\' y2=\'97\'%3E%3Cstop offset=\'0\' stop-color=\'%23000\' stop-opacity=\'0\'/%3E%3Cstop offset=\'1\' stop-color=\'%23000\' stop-opacity=\'1\'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cg fill=\'%23b3b1ca\' fill-opacity=\'0.22\'%3E%3Crect x=\'100\' width=\'100\' height=\'100\'/%3E%3Crect y=\'100\' width=\'100\' height=\'100\'/%3E%3C/g%3E%3Cg fill-opacity=\'0.22\'%3E%3Cpolygon fill=\'url(%23a)\' points=\'100 30 0 0 200 0\'/%3E%3Cpolygon fill=\'url(%23b)\' points=\'100 100 0 130 0 100 200 100 200 130\'/%3E%3C/g%3E%3C/svg%3E")',
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover'
          }}>
            <h1 style={{ color: 'black', margin: '10px 0', fontSize: isMobile ? '1.8rem' : '4.5rem', textAlign: 'center' }}>
              {clinicName}
            </h1>
            <h2 style={{ fontSize: isMobile ? '0.9em' : '1.2em', color: '#95a5a6', margin: '10px 0', textAlign: 'center' }}>
              {currentTime.toLocaleString('en-US', { timeZone: 'Africa/Kampala' })}
            </h2>

            {!selectedEmployee && (
              <div style={{
                backgroundColor: '#fff3cd', border: '1px solid #ffeeba',
                borderRadius: '5px', padding: '12px 20px', margin: '10px 0',
                color: '#856404', fontSize: isMobile ? '12px' : '16px', fontWeight: 'bold', textAlign: 'center'
              }}>
                ⚠️ Please select your name from the buttons below before performing any action on this platform
              </div>
            )}

            {/* Top action buttons */}
            <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { act: 'sales',             icon: faDollarSign, label: 'Cashier',             handler: () => handleButtonClick('sales') },
                { act: 'credits',           icon: faClipboardList, label: 'Credits',           handler: () => handleButtonClick('credits') },
                { act: 'selldrugs',         icon: faPills,      label: 'Selling', handler: () => handleButtonClick('selldrugs') },
              ].map(btn => (
                <button key={btn.act} style={actionBtnStyle(btn.act)} onClick={btn.handler} disabled={!selectedEmployee}
                  onMouseEnter={e => { if (selectedEmployee) e.currentTarget.style.transform = 'scale(1.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                  <FontAwesomeIcon icon={btn.icon} style={{ marginRight: '8px', fontSize: '1.2em' }} className="radiating-icon" />
                  {btn.label}
                </button>
              ))}
            </div>

            {/* Search */}
            {shouldShowSearchAndPagination && (
              <div style={{ marginTop: '10px', marginBottom: '10px', width: '100%', display: 'flex', justifyContent: 'center' }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  style={{
                    padding: '10px', width: isMobile ? '90%' : '300px',
                    borderRadius: '5px', border: '1px solid #ccc', fontSize: isMobile ? '14px' : '16px'
                  }}
                />
              </div>
            )}

            {/* Employee list - Centered grid for desktop */}
            <div ref={employeesContainerRef} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? getEmployeeGridColumns() : `repeat(${employeesPerRow}, minmax(120px, 1fr))`,
              gap: '12px',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '20px auto',
              maxWidth: '1200px',
              padding: '0 20px',
              width: '100%',
              maxHeight: isMobile ? '400px' : '400px',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#888 #f1f1f1'
            }}>
              {currentEmployees.map(employee => (
                <button
                  key={employee.Name}
                  onClick={() => handleEmployeeClick(employee.Name, action, employee.colour || 'blue')}
                  style={employeeButtonStyle(employee.Name)}
                >
                  <FontAwesomeIcon icon={faUser} style={{ marginRight: '8px' }} className="radiating-icon" />
                  {employee.Name}
                </button>
              ))}
            </div>

            {/* Pagination */}
            {shouldShowSearchAndPagination && filteredEmployees.length > employeesPerPage && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', gap: '5px', flexWrap: 'wrap' }}>
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={{
                  padding: isMobile ? '6px 10px' : '8px 12px', border: '1px solid #ddd',
                  backgroundColor: currentPage === 1 ? '#f8f9fa' : '#007bff',
                  color: currentPage === 1 ? '#6c757d' : 'white',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer', borderRadius: '4px'
                }}>Previous</button>
                {[...Array(Math.ceil(filteredEmployees.length / employeesPerPage)).keys()].slice(0, isMobile ? 5 : 10).map(n => (
                  <button key={n} onClick={() => setCurrentPage(n + 1)} style={{
                    padding: isMobile ? '6px 10px' : '8px 12px', border: '1px solid #ddd',
                    backgroundColor: currentPage === n + 1 ? '#007bff' : 'white',
                    color: currentPage === n + 1 ? 'white' : '#007bff',
                    cursor: 'pointer', borderRadius: '4px'
                  }}>{n + 1}</button>
                ))}
                <button
                  disabled={currentPage === Math.ceil(filteredEmployees.length / employeesPerPage)}
                  onClick={() => setCurrentPage(p => p + 1)}
                  style={{
                    padding: isMobile ? '6px 10px' : '8px 12px', border: '1px solid #ddd',
                    backgroundColor: currentPage === Math.ceil(filteredEmployees.length / employeesPerPage) ? '#f8f9fa' : '#007bff',
                    color: currentPage === Math.ceil(filteredEmployees.length / employeesPerPage) ? '#6c757d' : 'white',
                    cursor: currentPage === Math.ceil(filteredEmployees.length / employeesPerPage) ? 'not-allowed' : 'pointer',
                    borderRadius: '4px'
                  }}>Next</button>
              </div>
            )}
          </div>

          {/* Bottom action buttons */}
          <div style={{ marginTop: '5px', display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              { act: 'store',               icon: faShoppingCart, label: 'Store',          handler: handleStoreButtonClick },
              { act: 'contacts',            icon: faAddressBook,  label: 'Customer Details', handler: handleContactsButtonClick },
            ].map(btn => (
              <button key={btn.act} style={actionBtnSmStyle(btn.act)} onClick={btn.handler} disabled={!selectedEmployee}
                onMouseEnter={e => { if (selectedEmployee) e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                <FontAwesomeIcon icon={btn.icon} style={{ marginRight: '8px', fontSize: '1.2em' }} className="radiating-icon" />
                {btn.label}
              </button>
            ))}

            {/* Appointments */}
            <button style={actionBtnSmStyle('patient-appointments')}
              onClick={handlePatientAppointmentsButtonClick} disabled={!selectedEmployee}
              onMouseEnter={e => { if (selectedEmployee) e.currentTarget.style.transform = 'scale(1.05)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
              <FontAwesomeIcon icon={faCalendar} style={{ marginRight: '8px', fontSize: '1.2em' }} className="radiating-icon" />
              Reminders
              {appointmentCount > 0 && (
                <span style={{
                  backgroundColor: '#ec240e', color: '#ecf0f1',
                  borderRadius: '50%', padding: '5px 10px', marginLeft: '10px'
                }}>
                  {appointmentCount}
                </span>
              )}
            </button>
          </div>

          <footer style={{ marginTop: '20px', textAlign: 'center', color: '#6c757d', fontSize: isMobile ? '10px' : '14px', padding: '0 10px' }}>
            This system was created by DeepMind E-Systems. For support or help contact +256786747733
          </footer>
        </div>

        {/* ── Modals ── */}
        {showWelcomeModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <WelcomeModal onClose={() => setShowWelcomeModal(false)} />
          </div>
        )}

        {showAdminModal && (
          <AdminModal
            onClose={() => setShowAdminModal(false)}
            onConfirm={handleAdminConfirm}
            loading={loading}
            isMobile={isMobile}
          />
        )}

        {showModal && (
          <SecurityModal
            onClose={() => { setShowModal(false); setAction(null); }}
            onConfirm={(code) => { confirmAction(code); }}
            loading={loading}
            employeeName={selectedEmployee}
            showThemeToggle={true}
            currentTheme={selectedEmployeeTheme}
            onThemeChange={setSelectedEmployeeTheme}
            isMobile={isMobile}
          />
        )}

        {showSidebarModal && (
          <SecurityModal
            onClose={() => { setShowSidebarModal(false); setSidebarAction(''); }}
            onConfirm={(code) => { confirmSidebarAction(code); }}
            loading={sidebarLoading}
            employeeName={selectedEmployee}
            showThemeToggle={true}
            currentTheme={sidebarEmployeeTheme}
            onThemeChange={setSidebarEmployeeTheme}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
}

export default Dashboard;